import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type TCustomerRevenuesStep = "personalInfo" | "insurance";

/* ------------------------------ Step: Personal Info ------------------------------ */

export interface IPersonalInfoStep {
    name: string;
    lastName: string;
    gender: "FEMALE" | "MALE" | ""; // طبق همون مقادیر فعلی پروژه (FAMEL/MALE)
    phoneNumber: string;
    nationalCode: string;
}

/* ------------------------------ Step: Insurance ------------------------------ */

export interface IPlate {
    part1: string;
    part2: string; // حرف پلاک
    part3: string;
    part4: string;
}

// انواع بیمه‌ای که مربوط به خودرو/موتور هستن و باید بخش پلاک نمایش داده بشه
export const CAR_RELATED_INSURANCE_TYPES = ["ThirdParty", "CarBody", "MotorThirdParty"] as const;

export interface IInsuranceStep {
    insuranceType: string;
    dueDate: string;
    description: string;

    // فقط وقتی insuranceType یکی از CAR_RELATED_INSURANCE_TYPES باشه معنا داره
    plateType: "Meli" | "FreeZone" | "Engine" | "";
    plate: IPlate;
    plateSeries: string;
    plateNumber: string;
    freeZone: string;
    plateOwnerNationalCode: string;
}

/* ------------------------------- Root State -------------------------------- */

export interface ICustomerRevenuesState {
    step: TCustomerRevenuesStep;
    personalInfoStep: IPersonalInfoStep;
    insuranceStep: IInsuranceStep;
}

/* -------------------------------------------------------------------------- */
/* Initial State                                                              */
/* -------------------------------------------------------------------------- */

export const emptyPlate: IPlate = { part1: "", part2: "", part3: "", part4: "" };

const initialState: ICustomerRevenuesState = {
    step: "personalInfo",
    personalInfoStep: {
        name: "",
        lastName: "",
        gender: "",
        phoneNumber: "",
        nationalCode: ""
    },
    insuranceStep: {
        insuranceType: "",
        dueDate: "",
        description: "",
        plateType: "Meli",
        plate: { ...emptyPlate },
        plateSeries: "",
        plateNumber: "",
        freeZone: "",
        plateOwnerNationalCode: ""
    }
};

/* -------------------------------------------------------------------------- */
/* Slice                                                                      */
/* -------------------------------------------------------------------------- */

const CustomerRevenuesSlice = createSlice({
    name: "customerRevenues",
    initialState,
    reducers: {
        // ----- Navigation -----
        setStep: (state, action: PayloadAction<TCustomerRevenuesStep>) => {
            state.step = action.payload;
        },

        // ----- Personal Info -----
        setPersonalInfo: (
            state,
            action: PayloadAction<Partial<Omit<IPersonalInfoStep, "gender">>>
        ) => {
            state.personalInfoStep = { ...state.personalInfoStep, ...action.payload };
        },
        setGender: (state, action: PayloadAction<IPersonalInfoStep["gender"]>) => {
            state.personalInfoStep.gender = action.payload;
        },

        // ----- Insurance -----
        setInsuranceType: (state, action: PayloadAction<string>) => {
            state.insuranceStep.insuranceType = action.payload;
            // اگر بیمه‌ی جدید مربوط به خودرو/موتور نباشه، فیلدهای پلاک بی‌معنی میشن و پاک میشن
            if (!CAR_RELATED_INSURANCE_TYPES.includes(action.payload as any)) {
                state.insuranceStep.plateType = "Meli";
                state.insuranceStep.plate = { ...emptyPlate };
                state.insuranceStep.plateSeries = "";
                state.insuranceStep.plateNumber = "";
                state.insuranceStep.freeZone = "";
                state.insuranceStep.plateOwnerNationalCode = "";
            }
        },
        setInsuranceInfo: (
            state,
            action: PayloadAction<Partial<Pick<IInsuranceStep, "dueDate" | "description">>>
        ) => {
            state.insuranceStep = { ...state.insuranceStep, ...action.payload };
        },
        setPlateType: (state, action: PayloadAction<IInsuranceStep["plateType"]>) => {
            state.insuranceStep.plateType = action.payload;
            // با تغییر نوع پلاک، فیلدهای پلاک قبلی پاک میشن
            state.insuranceStep.plate = { ...emptyPlate };
            state.insuranceStep.plateSeries = "";
            state.insuranceStep.plateNumber = "";
            state.insuranceStep.freeZone = "";
        },
        setPlate: (state, action: PayloadAction<Partial<IPlate>>) => {
            state.insuranceStep.plate = { ...state.insuranceStep.plate, ...action.payload };
        },
        setPlateSeries: (state, action: PayloadAction<string>) => {
            state.insuranceStep.plateSeries = action.payload;
        },
        setPlateNumber: (state, action: PayloadAction<string>) => {
            state.insuranceStep.plateNumber = action.payload;
        },
        setFreeZone: (state, action: PayloadAction<string>) => {
            state.insuranceStep.freeZone = action.payload;
        },
        setPlateOwnerNationalCode: (state, action: PayloadAction<string>) => {
            state.insuranceStep.plateOwnerNationalCode = action.payload;
        },

        // ----- Reset -----
        resetCustomerRevenuesForm: () => initialState
    }
});

export const {
    setStep,
    setPersonalInfo,
    setGender,
    setInsuranceType,
    setInsuranceInfo,
    setPlateType,
    setPlate,
    setPlateSeries,
    setPlateNumber,
    setFreeZone,
    setPlateOwnerNationalCode,
    resetCustomerRevenuesForm
} = CustomerRevenuesSlice.actions;

export default CustomerRevenuesSlice.reducer;